/**
 * About Us ページ: KV タイトル（SplitText）+ 背景スライド・パララックス、Purpose アコーディオンほか
 *
 * KV イントロはデザイナー提供 aboutus.js（FV Timeline / 背景 HOLD）と recruit.js の KV と同パターン。
 *
 * @package Spice_Theme
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';

gsap.registerPlugin(ScrollTrigger, SplitText);

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
const prefersReducedMotion = () => REDUCED_MOTION.matches;

/**
 * KV 見出し・サブタイトルの FV アニメーション（fonts 読み込み後に実行）
 */
function initAboutusKvIntro() {
  const title = document.querySelector('.js-aboutus-kv-title');
  const subtitle = document.querySelector('.js-aboutus-kv-subtitle');

  if (!title) {
    return;
  }

  if (prefersReducedMotion()) {
    gsap.set(title, { opacity: 1 });
    if (subtitle) gsap.set(subtitle, { opacity: 1 });
    return;
  }

  const fvText = new SplitText(title, {
    type: 'chars',
    charsClass: 'chars',
  });

  gsap.set(title, { opacity: 1 });

  gsap.from(fvText.chars, {
    yPercent: 110,
    opacity: 0,
    stagger: 0.04,
    ease: 'power4.out',
    duration: 1,
    delay: 0.1,
  });

  if (subtitle) {
    gsap.fromTo(
      subtitle,
      { opacity: 0 },
      {
        opacity: 1,
        duration: 1,
        ease: 'power3.out',
        delay: 0.7,
      }
    );
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function getFocusableElements(container) {
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      el =>
        el.offsetParent !== null &&
        el.getAttribute('tabindex') !== '-1' &&
        !el.hasAttribute('disabled')
    );
  }

  function handleModalFocusTrap(e, modal) {
    if (e.key !== 'Tab') return;
    const focusable = getFocusableElements(modal);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  // --- Purpose アコーディオン（about.js 準拠：GSAP で高さ・opacity アニメーション）---
  const purposeItems = document.querySelectorAll('.p-aboutus__purpose__item');

  purposeItems.forEach((item, index) => {
    const trigger = item.querySelector('.p-aboutus__purpose__trigger');
    const panel = item.querySelector('.p-aboutus__purpose__panel');
    const content = item.querySelector('.p-aboutus__purpose__panel-content');

    if (!trigger || !panel || !content) {
      return;
    }

    const parts = [
      item.querySelector('.p-aboutus__purpose__description'),
      ...item.querySelectorAll('.p-aboutus__purpose__card'),
    ].filter(Boolean);

    // 初期（閉じ）
    panel.hidden = true;
    gsap.set(panel, { height: 0, overflow: 'hidden' });
    gsap.set(content, { opacity: 0, y: 12 });
    gsap.set(parts, { opacity: 0, y: 14 });

    const open = () => {
      gsap.killTweensOf([panel, content, ...parts]);

      item.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');

      panel.hidden = false;
      panel.style.height = 'auto';
      panel.style.visibility = 'hidden';

      gsap.set(content, { opacity: 0, y: 12 });
      gsap.set(parts, { opacity: 0, y: 14 });

      const targetH = panel.offsetHeight;
      panel.style.visibility = '';
      panel.style.height = '0px';

      gsap
        .timeline()
        .to(panel, {
          height: targetH,
          duration: 0.55,
          ease: 'power3.inOut',
          overwrite: 'auto',
        })
        .to(
          content,
          {
            opacity: 1,
            y: 0,
            duration: 0.35,
            ease: 'power3.out',
          },
          '-=0.25'
        )
        .to(
          parts,
          {
            opacity: 1,
            y: 0,
            duration: 0.45,
            ease: 'power3.out',
            stagger: 0.08,
          },
          '-=0.2'
        )
        .add(() => {
          panel.style.height = 'auto';
        });
    };

    const close = () => {
      gsap.killTweensOf([panel, content, ...parts]);

      trigger.setAttribute('aria-expanded', 'false');

      const currentH = panel.getBoundingClientRect().height;
      panel.style.height = `${currentH}px`;

      gsap.to([content, ...parts], {
        opacity: 0,
        y: 12,
        duration: 0.2,
        ease: 'power2.out',
        overwrite: 'auto',
      });

      gsap.to(panel, {
        height: 0,
        duration: 0.45,
        ease: 'power3.inOut',
        overwrite: 'auto',
        onComplete: () => {
          panel.hidden = true;
          item.classList.remove('is-open');
          panel.style.height = '0px';
        },
      });
    };

    trigger.addEventListener('click', () => {
      if (item.classList.contains('is-open')) {
        close();
      } else {
        open();
      }
    });

    // デフォルトで1個目を開く（about.js と同じ）
    if (index === 0) {
      open();
    }
  });

  // --- KV 背景スライドショー + スクロールパララックス ---
  const bgItems = gsap.utils.toArray('.p-aboutus__kv-bg-item');

  if (bgItems.length) {
    const FADE = 1.2;
    const HOLD = 3.0;

    gsap.set(bgItems, { opacity: 0 });
    gsap.set(bgItems[0], { opacity: 1 });

    const bgTl = gsap.timeline({ repeat: -1 });
    bgTl.to({}, { duration: HOLD });

    bgItems.forEach((item, i) => {
      const next = bgItems[(i + 1) % bgItems.length];

      bgTl
        .to(next, { opacity: 1, duration: FADE, ease: 'power2.out' })
        .to(item, { opacity: 0, duration: FADE, ease: 'power2.out' }, '<')
        .to({}, { duration: HOLD });
    });

    if (!prefersReducedMotion()) {
      gsap.to(bgItems, {
        scale: 1.15,
        y: -60,
        ease: 'none',
        scrollTrigger: {
          trigger: '.p-aboutus__kv',
          start: 'top top',
          end: 'bottom top',
          scrub: true,
        },
      });
    }
  }

  // --- KV テキスト スクロール文字アニメーション ---
  const kvText = document.querySelector('.js-aboutus-kv-text');
  if (kvText && !prefersReducedMotion()) {
    const split = new SplitText(kvText, { type: 'chars', charsClass: 'char' });
    gsap.set(split.chars, { opacity: 0.2 });
    gsap.to(split.chars, {
      opacity: 1,
      ease: 'none',
      stagger: 0.01,
      scrollTrigger: {
        trigger: kvText,
        start: 'top bottom',
        end: 'bottom bottom',
        scrub: true,
      },
    });
  }

  // --- js-fade-up: 下からフェードイン ---
  if (!prefersReducedMotion()) {
    gsap.utils.toArray('.js-fade-up').forEach(el => {
      gsap.set(el, { y: 40, opacity: 0 });
      gsap.to(el, {
        y: 0,
        opacity: 1,
        ease: 'power3.out',
        duration: 0.8,
        scrollTrigger: {
          trigger: el,
          start: 'top 85%',
          once: true,
        },
      });
    });
  }

  // --- 拠点セルクリックでモーダルを開く（内容は PHP で出力済み）---
  const locationCells = document.querySelectorAll('.js-location-cell');
  if (locationCells.length) {
    let openedByCell = null;
    let focusTrapHandler = null;

    const openModal = (modalId, triggerCell) => {
      const modal = document.getElementById(modalId);
      if (!modal) return;

      const bg = modal.querySelector('.p-aboutus__location-modal__bg');
      const contentItems = [
        modal.querySelector('.p-aboutus__location-modal__title'),
        modal.querySelector('.p-aboutus__location-modal__body'),
      ].filter(Boolean);

      gsap.set(modal, { opacity: 0 });
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      modal.setAttribute('aria-modal', 'true');
      modal.removeAttribute('inert');
      document.body.style.overflow = 'hidden';
      gsap.to(modal, { opacity: 1, duration: 0.3, ease: 'power2.out' });
      openedByCell = triggerCell;

      focusTrapHandler = e => handleModalFocusTrap(e, modal);
      document.addEventListener('keydown', focusTrapHandler);

      const closeBtn = modal.querySelector('.js-location-modal-close');
      closeBtn?.focus();

      if (bg) {
        gsap.set(bg, { clipPath: 'inset(100% 0% 0% 0%)' });
        gsap.to(bg, {
          clipPath: 'inset(0% 0% 0% 0%)',
          duration: 0.7,
          ease: 'expo.out',
        });
      }
      if (contentItems.length) {
        gsap.set(contentItems, { opacity: 0, y: 12 });
        gsap.to(contentItems, {
          opacity: 1,
          y: 0,
          duration: 0.52,
          ease: 'power3.out',
          stagger: 0.07,
          delay: 0.34,
        });
      }
    };

    const closeOpenModal = () => {
      const openModalEl = document.querySelector(
        '.p-aboutus__location-modal.is-open'
      );
      if (!openModalEl) return;

      if (focusTrapHandler) {
        document.removeEventListener('keydown', focusTrapHandler);
        focusTrapHandler = null;
      }

      const bg = openModalEl.querySelector('.p-aboutus__location-modal__bg');
      const contentItems = [
        openModalEl.querySelector('.p-aboutus__location-modal__title'),
        openModalEl.querySelector('.p-aboutus__location-modal__body'),
      ].filter(Boolean);

      gsap.to(openModalEl, {
        opacity: 0,
        duration: 0.15,
        ease: 'power2.out',
        onComplete: () => {
          openModalEl.classList.remove('is-open');
          openModalEl.setAttribute('aria-hidden', 'true');
          openModalEl.removeAttribute('aria-modal');
          openModalEl.setAttribute('inert', '');
          openModalEl.style.opacity = '';
          document.body.style.overflow = '';
          if (bg) gsap.set(bg, { clipPath: 'inset(100% 0% 0% 0%)' });
          if (contentItems.length)
            gsap.set(contentItems, { opacity: 0, y: 12 });
          if (openedByCell) {
            openedByCell.focus();
            openedByCell = null;
          }
        },
      });
    };

    locationCells.forEach(cell => {
      const modalId = cell.getAttribute('data-modal-id');
      if (!modalId) return;
      cell.addEventListener('click', () => openModal(modalId, cell));
      cell.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openModal(modalId, cell);
        }
      });
    });

    document.addEventListener('click', e => {
      if (e.target.closest('.js-location-modal-close')) {
        closeOpenModal();
      }
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') {
        const openModalEl = document.querySelector(
          '.p-aboutus__location-modal.is-open'
        );
        if (openModalEl) closeOpenModal();
      }
    });
  }

  // --- 拠点モーダル内スライダー（1枚表示・prev/next・ドットで切り替え）---
  document.querySelectorAll('.js-location-slider').forEach(slider => {
    const imgEl = slider.querySelector('.js-location-slider-img');
    const prevBtn = slider.querySelector('.js-location-slider-prev');
    const nextBtn = slider.querySelector('.js-location-slider-next');
    const dots = slider.querySelectorAll('.js-location-slider-dot');
    let currentIndex = 0;
    let images = [];
    try {
      const raw = slider.getAttribute('data-images');
      if (raw) images = JSON.parse(raw);
    } catch {
      // data-images が不正な場合は空配列のまま続行
      void 0;
    }

    if (!imgEl || !Array.isArray(images) || images.length === 0) return;

    const goTo = index => {
      const len = images.length;
      if (len === 0) return;
      currentIndex = ((index % len) + len) % len;
      const item = images[currentIndex];
      if (item && item.url) {
        imgEl.src = item.url;
        imgEl.alt = item.alt || '';
      }
      dots.forEach((dot, i) => {
        const isActive = i === currentIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    };

    if (prevBtn)
      prevBtn.addEventListener('click', () => goTo(currentIndex - 1));
    if (nextBtn)
      nextBtn.addEventListener('click', () => goTo(currentIndex + 1));
    dots.forEach(dot => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.getAttribute('data-index'), 10);
        if (!Number.isNaN(idx)) goTo(idx);
      });
    });
  });
});

document.fonts.ready.then(() => {
  initAboutusKvIntro();
});

/**
 * js-reveal-lines: 行マスク + 下から迫り上がる reveal。
 *
 * 設計方針（テキストが消える表示バグへの恒久対処）:
 * 1. SplitText は必ず Web フォント確定後に実行する。フォント未確定のまま分割すると
 *    行ボックスが誤計測され、reflow 後に行がマスク外へ押し出されたまま残る。
 * 2. reveal の発火は ScrollTrigger ではなく IntersectionObserver を使う。
 *    ScrollTrigger は本ページで位置計算が不安定になり、行が y:100% のマスク外に
 *    留まって本文が見えなくなる事象があったため、ブラウザネイティブで堅牢な
 *    IntersectionObserver に置き換える。
 * 3. observer もアニメーションも何らかの理由で不発になった場合に備え、一定時間後に
 *    必ず可視状態へ戻すフォールバック（setTimeout）を全要素に張る。これにより、
 *    どんな経路でも「本文が永久に消える」ことを防ぐ。
 */
function initRevealLines() {
  const reduce = prefersReducedMotion();

  gsap.utils.toArray('.js-reveal-lines').forEach(el => {
    let split;
    try {
      split = new SplitText(el, {
        type: 'lines',
        mask: 'lines',
        wordDelimiter: '',
      });
    } catch (err) {
      // 分割失敗時も本文が消えないよう、確実に可視状態へ戻す。
      console.warn('[aboutus] reveal-lines SplitText failed:', err);
      gsap.set(el, { clearProps: 'transform', opacity: 1 });
      return;
    }

    const lines = split.lines || [];

    // 親要素は base/_utility.scss で opacity:0（JS 前提の初期非表示・FOUC 対策）。
    // 分割できたら親を opacity:1 にして「表示可能」状態にする。文字自体は行マスク +
    // yPercent:100 で隠れているため、ここで opacity を上げても本文は出てこない。
    // これを忘れると行アニメが進んでも親が透明のままで本文が見えない（本バグの主因）。
    gsap.set(el, { opacity: 1 });

    // 動きを抑える設定なら即座に表示状態にして終了。
    if (reduce || lines.length === 0) {
      gsap.set(lines, { yPercent: 0 });
      return;
    }

    gsap.set(lines, { yPercent: 100 });

    let revealed = false;
    // animate=true: 通常の迫り上がりアニメ（rAF 依存）。
    // animate=false: 即時表示（gsap.set は同期適用なので rAF が止まっていても効く）。
    const reveal = animate => {
      if (revealed) return;
      revealed = true;
      if (animate) {
        gsap.to(lines, {
          yPercent: 0,
          duration: 1,
          stagger: 0.1,
          ease: 'power4.out',
        });
      } else {
        gsap.set(lines, { yPercent: 0 });
      }
    };

    const io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            reveal(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.2 }
    );
    io.observe(el);

    // 安全策: observer / アニメが不発でも本文が消えないよう、一定時間後に必ず即時表示。
    // gsap.set による同期適用なので、非表示タブで rAF が止まっていても確実に効く。
    setTimeout(() => {
      io.disconnect();
      reveal(false);
    }, 6000);
  });
}

function startRevealLines() {
  const run = () => {
    // 次フレームでレイアウト確定後に実行。非表示タブでは rAF が止まるため、
    // setTimeout フォールバックを併用して確実に一度だけ実行する。
    let done = false;
    const fire = () => {
      if (done) return;
      done = true;
      initRevealLines();
    };
    requestAnimationFrame(() => requestAnimationFrame(fire));
    setTimeout(fire, 200);
  };
  // Web フォント確定後に分割。document.fonts 未対応環境は即時実行（fallback）。
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(run);
  } else {
    run();
  }
}

// load が既に発火済み（モジュール評価が load 後）の場合は listener が二度と
// 発火しないため、readyState を見て即時実行する。これがないと reveal が走らず
// テキストが初期状態のまま残る。
if (document.readyState === 'complete') {
  startRevealLines();
} else {
  window.addEventListener('load', startRevealLines);
}
